// Every endpoint /st1/input-stat/new calls. 🔴 Same endpoints and request bodies as /st1/input-stat
// (tasks/st1-stattracker/CLAUDE.md → "Rules for this task": zero backend contract changes). The
// old call sites are cited on each function so the two can be compared line by line.
import httpHandler from '../../../helpers/httpHandler';
import {
  Category,
  ChartType,
  FormAssociatedField,
  MemberTeam,
  Squad,
  StatField,
  TeamForm,
  TeamFormSubmission,
  TypeConfig,
  UserFormSubmission,
  UserStat
} from './types';

export const LOCATION = 'input_chart';

const get = <T>(path: string, params?: object): Promise<T> =>
  httpHandler.get(path, params) as Promise<T>;
const post = <T>(path: string, body: object): Promise<T> =>
  httpHandler.post(path, body) as Promise<T>;

// Page shell (StatInputViewer.js:44-73, TeamSelectionElement.js:16-31)
export const findUserAsMemberOfTeam = () =>
  get<unknown[]>('/api/st1/customStats/findUserAsMemberOfTeam');
export const createTeam = (teamName: string) =>
  post('/api/st1/teamStats/createTeam', { teamName, auto_generated: true });
export const getMemberTeams = () =>
  get<MemberTeam[]>('/api/st1/teamStats/getMemberTeams');

// Stat definitions and card data (GraphViewer.js:127-152, TeamInputViewer.js:55-69)
export const getStatTrackingFields = (teamId: number) =>
  get<StatField[]>('/api/st1/customStats/getStatTrackingFields', { teamId });
export const getCategories = (teamId: number) =>
  get<Category[]>('/api/st1/customStats/getCategories', { teamId });
export const getSquads = (teamId: number) =>
  get<Squad[]>('/api/st1/teamStats/getSquads', { teamId });
export const getUserStats = (teamId: number, date: string) =>
  get<UserStat[]>('/api/st1/customStats/getUserStats', {
    startDate: date,
    endDate: date,
    teamId
  });

/** The write for every −, + and typed value: the NEW ABSOLUTE TOTAL, not a +1 (TeamInputViewer.js:120-130). */
export const recordStat = (
  team: MemberTeam,
  fieldId: number,
  statValue: number,
  date: string
) =>
  post('/api/st1/customStats/oldRecordStats', {
    statValue,
    date,
    teamId: team.team_id,
    fieldId,
    teamLeader: team
  });

// Card order, personal to the user (StatInputViewer.js:177-237)
export const getStatFieldOrder = (teamId: number) =>
  get<{ id: number; list_order: string } | null>(
    '/api/st1/financials/getStatFieldOrder',
    { teamId, location: LOCATION }
  );
export const updateStatFieldOrder = (
  teamId: number,
  id: number | null,
  order: number[]
) =>
  post<{ insertId?: number }>('/api/st1/financials/updateStatFieldOrder', {
    teamId,
    id,
    location: LOCATION,
    order: order.join(',')
  });

// Forms (StatInputViewer.js:125-176, 273-296)
export const getFormsForTeam = (teamId: number) =>
  get<TeamForm[]>('/api/st1/financials/getFormsForTeam', { teamId });
export const getAllFormAssociatedFieldsForTeam = (teamId: number) =>
  get<FormAssociatedField[]>(
    '/api/st1/financials/getAllFormAssociatedFieldsForTeam',
    { teamId }
  );
export const getUserFormSubmissions = (teamId: number, date: string) =>
  get<UserFormSubmission[]>('/api/st1/financials/getUserFormSubmissions', {
    teamId,
    date
  });
/** Deletes the entry AND takes 1 off the linked stat, in one server call. */
export const deleteFormSubmission = (
  teamId: number,
  submission: UserFormSubmission,
  date: string
) =>
  post('/api/st1/financials/deleteFormSubmission', {
    teamId,
    formId: submission.form_id,
    id: submission.submission_id,
    fieldId: submission.field_id,
    date
  });
export const getFormSubmissionsForTeam = (
  teamId: number,
  date: string,
  userId: number
) =>
  get<TeamFormSubmission[]>('/api/st1/financials/getFormSubmissionsForTeam', {
    teamId,
    date,
    userId
  });

// Chart data (GraphViewer.js:318-446). The response is [rows, rolesToShow].
export const getTeamMemberStats = (
  teamId: number,
  chartType: ChartType,
  endDate: string,
  startDate?: string | null
) =>
  get<[Record<string, unknown>[], string[]]>(
    '/api/st1/customStats/getTeamMemberStats',
    { teamId, endDate, startDate: startDate || null, chartType }
  );

// Chart settings, read (GraphViewer.js:154-291, 486-518)
export interface ChartOptionRow {
  chart_type: string;
  data_display: string | null;
  cur_date_option: string | null;
  prev_date_option: string | null;
  date_type: string | null;
  rolling_date_unit: string | null;
  rolling_date_value: number | string | null;
  show_yesterday: number | boolean | null;
  sort_by_field: string | null;
  stat_goal: number | string | null;
  view_squad: number | boolean | null;
  static_start_date: string | null;
  static_end_date: string | null;
}
export const getChartConfigSettings = (teamId: number) =>
  get<{ chart_type: ChartType; selected_data_point?: string | null }[]>(
    '/api/st1/customStats/getChartConfigSettings',
    { teamId, location: LOCATION }
  );
export const getChartOptionSettings = (teamId: number, chartType: ChartType) =>
  get<ChartOptionRow[]>('/api/st1/customStats/getChartOptionSettings', {
    teamId,
    location: LOCATION,
    chartType
  });
export const getAllDataPointSelections = (
  teamId: number,
  chartType: ChartType
) =>
  get<{ type: 'team' | 'squad' | 'member'; name: string }[]>(
    '/api/st1/customStats/getAllDataPointSelections',
    { teamId, location: LOCATION, chartType }
  );
export const getAllSelectedFieldsAtLocation = (teamId: number) =>
  get<
    {
      id?: number | null;
      name?: string;
      category_id?: number;
      category_name?: string;
    }[]
  >('/api/st1/customStats/getAllSelectedFieldsAtLocation', {
    teamId,
    location: LOCATION
  });

// Chart settings, write. Bodies copied from helpers/AdditionalStatHelper.js:469-578 and
// GraphViewer.js:1383-1408.
export const saveChartConfigSettings = (teamId: number, chartType: ChartType) =>
  post('/api/st1/customStats/saveChartConfigSettings', {
    teamId,
    location: LOCATION,
    chartType,
    statDisplayId: undefined,
    singleStatOption: undefined,
    coachingDashboardData: { isCoachingDashboard: undefined, clientId: 'new' }
  });

export const updateChartOptionSettings = (
  teamId: number,
  chartType: ChartType,
  c: TypeConfig
) =>
  post('/api/st1/customStats/updateChartOptionSettings', {
    settings: {
      teamId,
      location: LOCATION,
      chartType,
      showYesterday: c.showYesterday,
      sortByField: c.sortField,
      dataDisplay: c.dataDisplay,
      dateType: c.dateType,
      prevDateOption: c.prevOption,
      curDateOption: c.curOption,
      rollingUnit: c.rollingUnit,
      rollingValue: c.rollingValue,
      startDate: c.staticStart,
      endDate: c.staticEnd,
      statGoal: c.statGoal,
      viewSquad: c.squadView,
      statDisplayId: undefined
    }
  });

/** `field` is a series key: `Field_Name_<id>` or `Category_Name_<id>`. */
export const updateSavedStackedField = (
  teamId: number,
  field: string,
  checked: boolean
) => {
  const parts = field.split('_');
  const id = parseInt(parts[parts.length - 1]);
  const isCategory = parts[0] === 'Category';
  return post('/api/st1/customStats/updateSavedStackedFields', {
    teamId,
    statDisplayId: undefined,
    fieldId: isCategory ? null : id,
    location: LOCATION,
    categoryId: isCategory ? id : null,
    checked
  });
};

/** 🔑 The server TOGGLES the selection (adds it if missing, removes it if present). */
export const toggleDataPointSelection = (
  teamId: number,
  chartType: ChartType,
  type: 'team' | 'squad' | 'member',
  name: string
) => {
  const id =
    type === 'member'
      ? name.split('_')[name.split('_').length - 1]
      : name.split('~~')[1];
  return post('/api/st1/customStats/updateDataPointSelections', {
    teamId,
    type,
    name,
    typeId: parseInt(id),
    location: LOCATION,
    chartType,
    statDisplayId: undefined
  });
};
